import { Table } from "antd";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import PropTypes from "prop-types";

import { numberFormatter } from "@/utils";
import { colorSchemaList } from "@/utils/colorSchemas";
import LoadingOverlay from "@/components/Shared/LoadingOverlay";
import { useGetTableDataQuery } from "@/redux/api/dashboardApi";
import { getDashboardFilterDate } from "@/utils/globalUtils";

const TableChart = ({ data }) => {
  const {
    isTitleHidden,
    colorSchema,
    xAxis,
    operator,
    locations,
    chartTitle,
    timeRange,
    customOperator,
    interval,
    lastXDays,
    lastXDaysValue,
  } = data;

  const { dateRange } = useSelector((state) => state.dashboardV2);
  const ref = useRef({});

  const [tableColumn, setTableColumn] = useState([]);
  const [tableData, setTableData] = useState([]);

  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const [height, setHeight] = useState(100);
  const [width, setWidth] = useState(100);

  const schema = colorSchemaList.table?.find((item) => item.value === colorSchema);

  const StyledTable = styled(Table)`
    .ant-table-thead > tr > th,
    .ant-table-thead > tr > th.ant-table-column-has-sorters:hover {
      background-color: ${schema?.colors[0]} !important;
      color: ${schema?.colors[2]};
    }

    .ant-table-tbody > tr > td,
    .ant-table-tbody > tr > td.ant-table-cell-row-hover {
      background-color: ${schema?.colors[1]} !important;
      color: ${schema?.colors[3]};
    }
    .ant-table-body {
      max-width: ${width}px;
      min-width: 100%;
      max-height: ${height}px;
    }
  `;

  useEffect(() => {
    setWidth(ref.current?.clientWidth > 100 ? ref.current?.clientWidth : 100);
    setHeight(ref.current?.clientHeight - 130 > 100 ? ref.current?.clientHeight - 130 : 100);
  }, [ref.current?.clientWidth, ref.current?.clientHeight]);

  const generateTableHeader = (deviceParameters) => {
    setTableColumn(
      deviceParameters.map((name) => {
        if (name === "date") {
          return {
            title: "Time",
            render: (_, row) => moment(row?.[name]).format("DD/MM/YYYY hh:mm:ss a"),
            width: "200px",
            align: "center",
            sorter: (a, b) => moment(a?.[name]).toDate() - moment(b?.[name]).toDate(),
          };
        }
        return {
          title: name,
          width: "200px",
          render: (_, row) => (typeof Number(row?.[name]) === "number" ? numberFormatter(Number(row?.[name])) : "--"),
          align: "center",
          sorter: (a, b) => a[name] - b[name],
        };
      }),
    );
  };

  const params = React.useMemo(() => {
    let baseParams = {
      operator: operator || customOperator,
      limit,
      page,
      interval,
      peakHours: dateRange?.peakHours,
    };
    const deviceParameter = JSON.stringify({
      _id_in: (Array.isArray(locations?.yAxis) ? locations.yAxis : [locations?.yAxis]).filter(Boolean).join(","),
      populate: "systemFormula systemParameter",
    });

    const date = getDashboardFilterDate({ xAxis, lastXDaysValue, lastXDays, timeRange });

    return { ...baseParams, ...date, deviceParameter };
  }, [operator, customOperator, limit, page, interval, dateRange?.peakHours, locations, xAxis, lastXDaysValue, lastXDays, timeRange]);

  const { data: fetchedData, isFetching: isLoading } = useGetTableDataQuery(params, { skip: !params });

  useEffect(() => {
    if (fetchedData) {
      setTableData(fetchedData.results || []);
      if (fetchedData.tableColumns) {
        generateTableHeader(fetchedData.tableColumns);
      }
      setTotal(fetchedData.total || 0);
    }
  }, [fetchedData]);

  return (
    <div className="d-flex flex-column h-100 w-100 p-2 dashboard-table" key={ref.current?.clientWidth}>
      {!isTitleHidden && (
        <div className="ql">
          <div className="ql-editor p-0 pb-1" style={{ textAlign: "center" }}>
            <div dangerouslySetInnerHTML={{ __html: chartTitle }}></div>
          </div>
        </div>
      )}
      <div
        className="h-100 nodrag nopan nowheel"
        ref={ref}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
      >
        <LoadingOverlay isLoading={isLoading} className="flex-grow-1">
          <StyledTable
            columns={tableColumn}
            dataSource={tableData}
            bordered={true}
            showSorterTooltip={{
              target: "sorter-icon",
            }}
            // pagination={{
            //   showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
            // }}
            pagination={{
              defaultPageSize: limit,
              current: page,
              total,
              showSizeChanger: true,
              onShowSizeChange: (_, pageSize) => {
                setLimit(pageSize);
              },
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
              pageSizeOptions: [10, 20, 50],
              responsive: true,
            }}
            onChange={(pagination) => {
              setPage(pagination.current);
            }}
            scroll={{
              x: true,
              y: true,
            }}
          />
        </LoadingOverlay>
      </div>
    </div>
  );
};

export default TableChart;

TableChart.propTypes = {
  data: PropTypes.object.isRequired,
  mainDashboard: PropTypes.bool,
};
